import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';
import { usePointDetections } from '../../services/data/pointDetections/hooks/usePointDetections';
import { useBlock } from '../../services/entities/hooks/useBlock';
import { useGetCustomerQuery } from '../../services/entities/impl/blfApi/endpoints/customers';
import { useGetRanchQuery } from '../../services/entities/impl/blfApi/endpoints/ranches';
import { useGetScanByIdQuery } from '../../services/entities/impl/blfApi/endpoints/scans';
import { useGetUserQuery } from '../../services/entities/impl/blfApi/endpoints/users';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import BuildScanDataButton from '../scanDataApi/BuildScanDataButton';
import useFilterScanData, {
  ScanDataFilterEnum,
} from '../scanDataApi/hooks/useFilterScanData';
import { ScanData } from '../scanDataApi/scanDataDefinitions';
import { useJsonLabelsPoints } from './collections/hooks/labels/useJsonLabelsPoints';
import DebugInfoSection from './components/DebugInfoSection';
import { DebugInfoSimpleInfoItem } from './components/DebugInfoSimpleInfoItem';
import { CollectionDebugSection } from './ranchAndBlock/CollectionsDebugSection';
import { concatenateItemNames } from './utils/concatenateItemNames';

export type DebugInfoProps = PropsWithChildren<{
  customerID?: string;
  ranchID?: string;
  blockID?: string;
  scanID?: string;
  pointID?: string;
  debugInfoUserID?: string;
}>;

const DebugInfo = ({
  customerID = '',
  ranchID = '',
  blockID = '',
  scanID = '',
  pointID = '',
  debugInfoUserID = '',
  children,
}: DebugInfoProps) => {
  const { Developer } = useFeatureFlags();

  const currPointScanDataResponse = useFilterScanData({
    activeFilters: [ScanDataFilterEnum.point_id],
    disableClassFilter: true, // Fetch all classes so we can list all in debug info
  });

  const pointLabels = useJsonLabelsPoints(
    currPointScanDataResponse.data as ScanData[]
  );

  const {
    currentData: customer,
    isLoading: isLoadingCustomer,
    isFetching: isFetchingCustomer,
  } = useGetCustomerQuery(customerID, {
    skip: !customerID,
  });

  const {
    currentData: ranch,
    isLoading: isLoadingRanch,
    isFetching: isFetchingRanch,
  } = useGetRanchQuery(ranchID, {
    skip: !ranchID,
  });

  const { block, isBlockLoading } = useBlock(blockID, {});

  const {
    currentData: scan,
    isLoading: isLoadingScan,
    isFetching: isFetchingScan,
  } = useGetScanByIdQuery(scanID, {});

  const { pointDetections, isPointDetectionsLoading } = usePointDetections(
    pointID,
    scanID
  );

  const {
    currentData: user,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useGetUserQuery(debugInfoUserID, {
    skip: !debugInfoUserID,
  });

  const pathName = useMemo(
    () => concatenateItemNames(customer, ranch, block, scan),
    [customer, ranch, block, scan]
  );

  // A scan can be part of multiple collections, so we need to fetch each one individually
  const collections = useMemo(() => {
    const collectionIDs = scan?.collectionIDs ?? [];
    if (collectionIDs.length === 0) {
      return null;
    }

    const numOfCollections = collectionIDs.length ?? 0;
    if (numOfCollections > 0) {
      return collectionIDs.map((collectionID, idx) => (
        <CollectionDebugSection
          key={collectionID}
          collectionID={collectionID}
          itemNumber={idx}
          itemsTotal={numOfCollections}
        />
      ));
    }
  }, [scan?.collectionIDs]);

  const content = (
    <Box
      sx={{
        width: '35rem',
        height: 'calc(100% - 3.5rem)', // Subtracting header height
        maxHeight: 'calc(100% - 3.5rem)', // Subtracting header height
        boxSizing: 'border-box', // Include padding and border in box height
        p: '0.5rem',
        overflowY: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <DebugInfoSimpleInfoItem
        label='Path'
        value={pathName}
        showCopyButton={!!pathName}
      />

      <DebugInfoSection
        sectionLabel={'Customer'}
        name={customer?.name}
        id={customer?.id}
        isLoading={isLoadingCustomer || isFetchingCustomer}
        jsonObjectOrArrayOfObjects={customer}
      />
      <DebugInfoSection
        sectionLabel={'Ranch'}
        name={ranch?.name}
        id={ranch?.id}
        isLoading={isLoadingRanch || isFetchingRanch}
        jsonObjectOrArrayOfObjects={ranch}
      />
      <DebugInfoSection
        sectionLabel={'Block'}
        name={block?.name}
        id={block?.id}
        isLoading={isBlockLoading}
        jsonObjectOrArrayOfObjects={block}
      />
      {user && (
        <>
          <DebugInfoSection
            sectionLabel={'User'}
            name={user?.email}
            id={user?.id}
            isLoading={isLoadingUser || isFetchingUser}
            jsonObjectOrArrayOfObjects={user}
          />
          <Divider />
        </>
      )}
      {collections && collections}
      {scanID && (
        <>
          <DebugInfoSection
            sectionLabel={'Scan'}
            name={scan?.scanDate}
            id={scan?.id}
            isLoading={isLoadingScan || isFetchingScan}
            jsonObjectOrArrayOfObjects={scan}
          />
        </>
      )}
      {pointID && (
        <>
          <DebugInfoSection
            sectionLabel={'Point ID / Scan Data'}
            id={pointID}
            isLoading={currPointScanDataResponse.isLoading}
            jsonObjectOrArrayOfObjects={currPointScanDataResponse.data}
            jsonLabels={pointLabels}
            actionButtonOrElement={<BuildScanDataButton />}
          />
          <DebugInfoSection
            sectionLabel={'Point Detections / Masks'}
            id={pointID}
            isLoading={isPointDetectionsLoading}
            jsonObjectOrArrayOfObjects={pointDetections}
            showID={false}
          />
        </>
      )}
      {children}
      {Developer && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Typography>Force Error to test Sentry</Typography>
          <IconButton
            onClick={() => {
              throw new Error('Manually triggered error');
            }}
          >
            <ReportGmailerrorredIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return content;
};

export default DebugInfo;
